import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Decode JWT token to get user info
  const decodeToken = (token) => {
    try {
      const decoded = jwtDecode(token);
      return {
        id: decoded.userId,
        role: decoded.role,
        gameUsername: decoded.gameUsername,
        username: decoded.username,
        permissions: decoded.permissions
      };
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  };

  // Check for token and decode user info
  useEffect(() => {
    if (token) {
      const userInfo = decodeToken(token);
      if (userInfo) {
        setUser(userInfo);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } else {
        // Invalid token, remove it
        localStorage.removeItem('token');
        setToken(null);
      }
    }
    setLoading(false);
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/api/user/profile');
      setUser(response.data.user);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      console.log('AuthContext login called with:', { username, password });
      
      const response = await axios.post('/api/auth/login', {
        username,
        password
      });
      
      console.log('Server response:', response.data);
      
      const { token: newToken, user: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('AuthContext login error:', error.response?.data || error);
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در ورود'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/auth/register', userData);
      
      // Registration successful and user is automatically active
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در ثبت نام'
      };
    }
  };

  const sendVerificationCode = async (phone) => {
    try {
      const response = await axios.post('/api/auth/send-code', { phone });
      return { success: true, message: response.data.message };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در ارسال کد تایید'
      };
    }
  };

  const verifyCode = async (phone, code) => {
    try {
      const response = await axios.post('/api/auth/verify', { phone, code });
      
      const { token: newToken, user: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'خطا در تایید کد'
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    sendVerificationCode,
    verifyCode,
    logout,
    updateUser,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'superadmin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
